import { useEffect, useRef, useState } from "react";
import "./index.css";
import BYOKstatus from "../BYOKstatus";
import { useDispatch, useSelector } from "react-redux";
import {
  cloudKeyList,
  decryptCloudKeys,
  encryptCloudKeys,
  getEncryptionKeys,
} from "../../../../redux/features/byok/byok";
import Loader from "../../../Loader/Index";
import TEXT from "../../../../utils/globalText";
import PrimaryButton from "../../../_utils/CommonButtons/PrimaryButton";
import TextInputField from "../../../_utils/CommonInputFields/TextInputField";
import ICONS from "../../../../assets/Icons";
import SVGComponent from "../../../_utils/SVGComponent";

const confirmationData = [
  {
    id: 1,
    title: "How BYOK works",
    text: [
      "Get your encryption key: Generate or retrieve your encryption key from your key management system (KMS) or security provider (e.g., using a service like AWS KMS).",
      "Integrate here: Enter the key details and integrate it into our system to encrypt banking credential data.",
      "You’re all set: Once your key is linked, the system will start encrypting your data using your own key. Only you (or those you authorize) have full control over the key, including managing access, rotation, and revocation.",
    ],
  },
  {
    id: 2,
    title: "Benefits of BYOK",
    text: [
      "Full Control: Use your own encryption key to secure your data. (access, rotation, revocation).",
      "Enhanced Security: Your key stays with you, only you can access your sensitive data. Protects and safeguards data from unauthorized access.",
      "Seamless Operations Efficiency: Keep your system running smoothly while protecting your banking credentials.",
      "Flexibility: Easily update or rotate keys as needed.",
      "Improved Compliance: Meets security and regulatory requirements.",
      "Seamless Integration: Works with your existing key management system.",
    ],
  },
  {
    id: 3,
    title: "Consequences of BYOK",
    text: [
      "Increased Responsibility: You’re responsible for managing, rotating, and securing your encryption key.",
      "No Provider Access: The service provider cannot access your encrypted data, limiting their ability to troubleshoot or assist with key management.",
      "Key Management Complexity: Requires integration with your key management system and ongoing monitoring.",
      "Potential for Service Disruption: If the key is lost or revoked incorrectly, it could lead to loss of data access.",
      "Cannot Revert Back To Our Key: Once data is decrypted on our end, we cannot re-encrypt it using our key. To maintain security, you must re-encrypt the data using your own encryption key.",
    ],
  },
];

const Accordion = ({ title, text, onToggle, isOpen }) => {
  const [isFullyClosed, setIsFullyClosed] = useState(true);

  const toggleAcc = () => {
    if (isOpen) {
      setTimeout(() => setIsFullyClosed(true), 300);
    } else {
      setIsFullyClosed(false);
    }
    onToggle();
  };

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => setIsFullyClosed(true), 300);
    } else {
      setIsFullyClosed(false);
    }
  }, [isOpen]);
  return (
    <div className="global-hover-white relative z-z-index-base bg-primary-nav-act rounded-border-radius-sm">
      <div
        className={` w-full h-[var(--category-icon-height)] flex flex-row items-start justify-between accordion-title cursor-pointer ${
          isOpen
            ? "title-open"
            : isFullyClosed
              ? "title-close"
              : "title-closing"
        }`}
        onClick={toggleAcc}
      >
        <h3 className="text-text-primary text-font-sm font-semibold">
          {title}
        </h3>
        <img
          src={
            isOpen
              ? ICONS?.BLUE_ARROW_UP_WHITE_CIRCLE
              : ICONS?.BLUE_ARROW_DOWN_WHITE_CIRCLE
          }
          alt={isOpen ? "up-arrow" : "down-arrow"}
          className="w-icon-md h-icon-md"
        />
      </div>
      <div
        className={`border-t-border border-primary-bg w-[calc(width-full-2*var(--margin-sm))] mx-margin-sm ${isFullyClosed ? "hidden" : "flex"}`}
      ></div>
      <div
        className={`accordion-list ${
          isOpen
            ? "accordion-open"
            : isFullyClosed
              ? "accordion-close"
              : "accordion-closing"
        }`}
      >
        <ol
          type="1"
          className={`accordion-text ${isOpen ? "open" : ""}`}
        >
          {text.map((point, index) => {
            const [highlight, ...rest] = point.split(/:(.*)/);

            return (
              <li
                key={index}
                className="text-text-primary text-font-xs font-semibold"
              >
                <span className="text-text-primary text-font-xs font-semibold">
                  {highlight}:
                </span>{" "}
                <span className="text-text-primary text-font-xs font-normal">
                  {rest}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

const BYOKconfirmationPopup = ({
  confirmationPopup,
  setConfirmationPopup,
  setCurrentDisplay,
  byokProcessPending,
  byokProcessFailed,
  setCredsExists,
  isLoading,
  setIsLoading,
  isFocus,
  setIsFocus,
}) => {
  const [inputText, setInputText] = useState("");

  const enableConfirm = inputText === "I have read & understood BYOK";
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && enableConfirm) {
        setIsLoading(true);
        setTimeout(() => {
          setCredsExists(false);
          setCurrentDisplay("BYOKstepperPage");
          setIsLoading(false);
        }, 2000);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enableConfirm, setCurrentDisplay]);

  return (
    <div
      className={`byok-conf-popup bg-primary-card ${confirmationPopup ? "popup-show" : ""}`}
    >
      <div className="flex flex-col gap-gap-md w-full">
        <div className="flex flex-col gap-gap-sm">
          <div className="flex gap-gap-sm items-center">
            <img
              src="https://q2pay.in/new_assets/common/dashboard/alert.svg"
              className="w-icon-md h-icon-md"
              alt="alert-img"
            />
            <p className="text-error text-font-lg font-semibold">
              {TEXT?.CONFIRM_KNOWLEDGE}
            </p>
          </div>
          <p className="text-text-primary text-font-md font-normal">
            {TEXT?.CONFIRMATION_NOTICE_TEXT}
          </p>
          <ul className="text-text-secondary text-font-sm font-normal flex flex-col gap-gap-sm list-disc pl-padding-md">
            <li>{TEXT?.BYOK_INFO_MESSAGE}</li>
            <li>{TEXT?.BYOK_BENEFITS_INFO}</li>
          </ul>
        </div>
        <div className="flex flex-col gap-gap-md">
          <p className="text-font-md font-normal text-text-primary">
            To confirm, kindly type{" "}
            <span className="font-semibold select-none">
              {" "}
              “I have read & understood BYOK”{" "}
            </span>{" "}
            in the input field below.
          </p>
          <div className="w-full">
            <TextInputField
              variant={enableConfirm ? "verified" : "default"}
              placeholder={"Enter text"}
              fontKind={"big"}
              bgColor="var(--primary-card)"
              fgColor="var(--text-primary)"
              value={inputText}
              name={"Entertext"}
              onInput={(e) => {
                setInputText(e.target.value);
              }}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              isFullWidth={true}
            />
          </div>
        </div>
        <div className="flex gap-gap-sm justify-end">
          {isLoading ? (
            <Loader height="var(--loader-md)" />
          ) : (
            <>
              <PrimaryButton
                btnText={TEXT?.CANCEL}
                btnType="button"
                handleFunction={() => {
                  setConfirmationPopup();
                  setInputText("");
                  setIsFocus(false);
                }}
                isDisabled={false}
                enabledBgColor="var(--primary-card)"
                enabledTextColor="var(--text-primary)"
                disabledTextColor="var(--text-primary)"
                hoverOption="white"
                outLineColor={"var(--text-primary)"}
              />
              <PrimaryButton
                btnText={TEXT?.CONFIRM}
                btnType="button"
                handleFunction={() => {
                  if (!enableConfirm) return;
                  setIsLoading(true);
                  setTimeout(() => {
                    setCredsExists(false);
                    setCurrentDisplay("BYOKstepperPage");
                    setIsLoading(false);
                  }, 1000);
                }}
                isDisabled={isLoading || !enableConfirm}
                enabledBgColor="var(--positive-act)"
                disabledBgColor="var(--text-disabled)"
                enabledTextColor="var(--text-primary)"
                inActiveVariantNeeded
                hoverOption="black"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BYOKconfirmation = ({
  setCurrentDisplay,
  byokProcessFailed,
  byokProcessPending,
  setCredsExists,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState(false);

  const [openIdx, setOpenIdx] = useState(null);
  useEffect(() => {
    if (confirmationPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "scroll";
    }

    return () => {
      document.body.style.overflow = "scroll";
    };
  }, [confirmationPopup]);
  return (
    <>
      <div className="z-z-index-modal overflow-y-scroll">
        {confirmationPopup && (
          <div
            className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-modal"
            style={{
              backgroundColor: "var(--PopUp-BackDrop)",
              opacity: "var(--dark-opacity)",
            }}
          ></div>
        )}
        <BYOKconfirmationPopup
          confirmationPopup={confirmationPopup}
          setConfirmationPopup={() => setConfirmationPopup(false)}
          setCurrentDisplay={setCurrentDisplay}
          byokProcessPending={byokProcessPending}
          byokProcessFailed={byokProcessFailed}
          setCredsExists={setCredsExists}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isFocus={isFocus}
          setIsFocus={setIsFocus}
        />
      </div>

      <div className="flex flex-col gap-gap-md py-padding-md">
        <section className="byok-confirmation-first-sec bg-primary-card p-padding-md rounded-border-radius-md flex Qlg:flex-row flex-col gap-gap-xl">
          <div className="flex flex-col">
            <div className="flex flex-col gap-gap-sm mb-margin-md">
              <h3 className="text-text-primary text-font-md font-semibold">
                {TEXT?.BYOK_TITLE}
              </h3>
              <p className="text-text-secondary text-font-xs font-normal">
                {TEXT?.BYOK_PRE_SETUP_NOTE}
              </p>
            </div>
            <div style={{ width: "fit-content" }}>
              <PrimaryButton
                btnText="Set BYOK"
                btnType="button"
                handleFunction={() => setConfirmationPopup(true)}
                isDisabled={false}
                enabledTextColor="var(--text-primary)"
                disabledTextColor="var(--text-primary)"
                enabledBgColor="var(--positive-act)"
                disabledBgColor="var(--text-disabled)"
                isLoading={false}
                hoverOption="white"
              />
            </div>
          </div>
          <img
            src={byokProcessFailed ? ICONS?.BYOK_FAILED : ICONS?.SET_BYOK}
            alt="Illustration showing BYOK data security"
            style={{ width: "fit-content" }}
          />
        </section>
        <section
          className="bg-primary-card p-padding-md rounded-border-radius-md flex flex-col gap-gap-md"
          // style={{
          //   zIndex: !confirmationPopup && "1",
          // }}
        >
          {confirmationData.map(({ id, title, text }) => (
            <Accordion
              key={id}
              title={title}
              text={text}
              id={id}
              onToggle={() => setOpenIdx(openIdx === id ? null : id)}
              isOpen={openIdx === id}
            />
          ))}
        </section>
      </div>
    </>
  );
};

const ByokDashboard = ({
  setCurrentDisplay,
  setServiceSelected,
  setCredentialsFocus,
  setCredentials,
  setCredsExists,
  setActiveStep,
  canEncrypt,
  setCanEncrypt,
  isBYOKEncProcessing,
  setIsBYOKEncProcessing,
  selectKey,
  setBankingSelected,
  setSelectKey,
  isRotatingKeys,
  setisRotatingKeys,
  currentFetchedKey,
  setCurrentFetchedKey,
  setRotatingKey,
  encryptedKey,
  setIsUpdateKey,
  isUpdateKey,
  updatedKeyDone,
  setUpdatedKeyDone,
  isChangeKeyFlowComplted,
  isEncryptDone,
}) => {
  const [byokProcessPending, setByokProcessPending] = useState(false);
  const [byokProcessFailed, setByokProcessFailed] = useState(false);
  const [isKeyDecrypted, setIsKeyDecrypted] = useState(false);

  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const dispatch = useDispatch();

  const {
    encryptCloudKeysLoading,
    encryptCloudKeysData,
    encryptCloudKeysError,
    s,
  } = useSelector((state) => state.byok);

  //useEffect -- thought process inside
  //

  useEffect(() => {
    const fetchEncryptionKeys = async (showLoader = false) => {
      if (showLoader) setLoading(true);

      try {
        // Step 1: Token checks

        // Step 2: Fetch key list & trigger encryption attempt
        const listOfKeyResponse = await dispatch(
          cloudKeyList({ showToast: false }),
        );
        const encrResponse = await dispatch(encryptCloudKeys(""));

        const isEncryptQueued =
          encrResponse?.message === "Encryption Queued successfully." ||
          encryptCloudKeysData?.message === "Encryption Queued successfully.";

        const isEncryptDone =
          encrResponse?.payload?.message === "Data is encrypted successfully.";

        //We hit encryption api to check if the key is encrypted, if yes, we wont hit Decrypt API (below one)and wont show 'Data at high risk' label in RED on dashboard
        //else we will hit decrypt api

        // Step 3: Decrypt fallback if no encrypt queued or done
        if (!isEncryptQueued && !isEncryptDone) {
          const deccrResponse = await dispatch(
            decryptCloudKeys({
              values: listOfKeyResponse?.payload.encryption_key,
              showToast: false,
            }),
          );

          // Decryption triggered because encryption wasn't in progress or done
          const isDecryptSuccess =
            deccrResponse.message === "Decryption Queued successfully." ||
            encrResponse.payload !== "Credentials Not Found";

          if (isDecryptSuccess) {
            setIsKeyDecrypted(true);
            setCanEncrypt(false);
            setIsBYOKEncProcessing(false);
            setByokProcessPending(false);
            setByokProcessFailed(true);
            clearInterval(intervalRef.current);
            return;
          }
        }

        // from the above code we get to know that key is not decrypted, which means either key is already encrypted or encryption is not done yet.

        // Now in Encryption flow process (if started) - We have to show Resume label (if user left the encryption in the middle, like when he updated his credentials and fetched keys but didn't encrypt key by leaving the process in the middle  ) or Failed label if encrypt btn clicked and key encryption process started but the process failed

        //on the first page (of dashboard) (BYOK confirmation page)
        //setByokProcessPending - this state is for showing Resume label (which gets true when keys are fetched)

        //on the last page (of dashboard) (BYOK Status page)
        // setIsBYOKEncProcessing - this state is for byok key encryption processing (which starts true when user clicks on encrypt button after completing the process )
        //setByokProcessFailed - this state is for and get true when the encryption process gets failed

        // Step 4: Encryption in progress
        if (encrResponse?.payload === "Encryption process is on going") {
          const keysResponse = await dispatch(
            cloudKeyList({ showToast: false }),
          );

          if (keysResponse?.payload?.encryption_key) {
            setCurrentFetchedKey(keysResponse?.payload?.encryption_key);
          }

          setCanEncrypt(true);
          setIsBYOKEncProcessing(true);
        }

        // Step 5: Encryption done
        else if (
          encrResponse.payload.message === "Data is encrypted successfully."
        ) {
          const keysResponse = await dispatch(
            cloudKeyList({ showToast: false }),
          );

          if (keysResponse?.payload?.encryption_key) {
            setCurrentFetchedKey(keysResponse?.payload?.encryption_key);
          }

          setCanEncrypt(true);
          setIsBYOKEncProcessing(false);
          clearInterval(intervalRef.current);
        }

        // Step 6: Other fallbacks (no encryption or error cases)
        else {
          const response = await dispatch(cloudKeyList({ showToast: false }));
          const errorMsg = response?.payload;

          const keysFound =
            response?.payload?.message === "Keys retrieved successfully." &&
            Array.isArray(response.payload?.keys) &&
            response.payload.keys.length > 0;

          if (keysFound) {
            setByokProcessPending(true);
            setByokProcessFailed(false);
            setCanEncrypt(false);
            clearInterval(intervalRef.current);
          } else if (
            errorMsg === "Credentials Not Found" ||
            errorMsg ===
              "Failed to initialize Client. Retry or Check if your Cloud Credentials are correct again"
          ) {
            setByokProcessFailed(false);
            setByokProcessPending(false);
            setCanEncrypt(false);
            clearInterval(intervalRef.current);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (showLoader) setLoading(false);
      }
    };

    // Initial call with loader
    fetchEncryptionKeys(true);

    // Set up polling every 5 seconds
    intervalRef.current = setInterval(() => fetchEncryptionKeys(false), 5000);

    // Clear polling on unmount
    return () => clearInterval(intervalRef.current);
  }, []);

  const fetchCreadentials = async () => {
    const expectedKeys = [
      "access_key",
      "secret_access_key",
      "session_token",
      "region",
      "platform",
    ];

    try {
      const response = await dispatch(getEncryptionKeys());
      const creds = response.payload?.data?.creadentials;

      const allKeysExist = expectedKeys.every((key) =>
        creds?.hasOwnProperty(key),
      );

      if (allKeysExist) {
        setServiceSelected(true);
        setCredentialsFocus((prev) => {
          return {
            region: true,
            access_key: true,
            secret_access_key: true,
            session_token: true,
          };
        });

        setCredsExists(true);

        setCredentials((prev) => {
          return {
            access_key: creds.access_key,
            secret_access_key: creds.secret_access_key,
            session_token: creds.session_token,
            region: creds.region,
            platform: creds.platform,
          };
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div className="byok_dashboard_container">
        <div className="bg-primary-nav w-full fixed z-z-index-header">
          <div className="Qlg:w-max-content-width w-full mx-auto Qlg:px-padding-base px-padding-md py-padding-md flex flex-col gap-gap-xs">
            <h3 className="text-text-primary text-font-sm font-semibold">
              {TEXT?.BYOK_ENCRYPTION}
            </h3>
            <p className="text-text-secondary text-font-xs font-normal">
              {TEXT?.BYOK_INTRO}
            </p>
          </div>
        </div>

        <div
          className={`Qsmd:pt-byok-data-risk-padding-top  w-full z-z-index-content fixed ${
            byokProcessFailed && !canEncrypt
              ? "bg-custom-red Qsm:pt-[102px] pt-[120px]"
              : byokProcessPending && !canEncrypt
                ? "bg-Highlight-orange pt-byok-padding"
                : ""
          }`}
        >
          <div className="Qlg:w-max-content-width w-full mx-auto py-padding-md Qlg:px-padding-base px-padding-md">
            {byokProcessPending && !canEncrypt && (
              <div className="flex flex-col gap-gap-sm">
                <div className="flex flex-col gap-gap-xs">
                  <h3>{TEXT?.CONTINUE_WHERE_LEFT_OFF}</h3>
                  <p>{TEXT?.RESUME_PROCESS_HINT}</p>
                </div>
                <button
                  onClick={() => {
                    fetchCreadentials();
                    setCurrentDisplay("BYOKstepperPage");
                    setActiveStep(1);
                  }}
                >
                  {TEXT?.RESUME_SETUP}
                  <SVGComponent
                    url={ICONS?.ARROW_RIGHT_WHITE}
                    color={"var(--text-primary)"}
                  />
                </button>
              </div>
            )}
            {byokProcessFailed && !canEncrypt && (
              <div className="flex flex-col gap-gap-xs">
                <p className="text-text-primary text-font-md font-semibold">
                  {isKeyDecrypted
                    ? "Data at high risk"
                    : "BYOK encryption - Failed"}
                </p>
                <p className="text-text-primary text-font-xs font-normal">
                  {isKeyDecrypted
                    ? `Currently your data is decrypted and at high risk, consider setting up your data with new key.`
                    : `The encryption process was unsuccessful. This may be due to a
                 session timeout, incorrect key details, or a system issue.
                 Kindly retry again.`}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className={`w-full`}>
          <div className="w-full mx-auto">
            <div className="">
              {loading ? (
                <div
                  className="flex items-center justify-center h-full-height"
                  // style={{
                  //   position: "fixed",
                  //   left: "var(--left-half)",
                  //   top: "var(--top-half)",
                  //   transform:
                  //     "translate(-var(--left-half), -var(--left-half))",
                  // }}
                >
                  <Loader />
                </div>
              ) : canEncrypt ? (
                <div className="Qmd:pt-[calc(var(--graphic-byok-img-mobile-height)+var(--border))] pt-[var(--byok-padding)] pb-byok-confirm-padding h-full-height overflow-scroll">
                  <BYOKstatus
                    isBYOKEncProcessing={isBYOKEncProcessing}
                    selectKey={selectKey}
                    currentFetchedKey={currentFetchedKey}
                    setCurrentDisplay={setCurrentDisplay}
                    setCanEncrypt={setCanEncrypt}
                    setByokProcessPending={setByokProcessPending}
                    setBankingSelected={setBankingSelected}
                    setSelectKey={setSelectKey}
                    setActiveStep={setActiveStep}
                    fetchCreadentials={fetchCreadentials}
                    isRotatingKeys={isRotatingKeys}
                    setisRotatingKeys={setisRotatingKeys}
                    setRotatingKey={setRotatingKey}
                    encryptedKey={encryptedKey}
                    setIsUpdateKey={setIsUpdateKey}
                    setIsKeyDecrypted={setIsKeyDecrypted}
                    setByokProcessFailed={setByokProcessFailed}
                    setCredentials={setCredentials}
                    updatedKeyDone={updatedKeyDone}
                    setUpdatedKeyDone={setUpdatedKeyDone}
                    isChangeKeyFlowComplted={isChangeKeyFlowComplted}
                    isEncryptDone={isEncryptDone}
                  />
                </div>
              ) : (
                <div
                  className={`w-full bg-primary-bg Qlg:px-padding-base px-padding-md ${
                    (byokProcessFailed && !canEncrypt) ||
                    (byokProcessPending && !canEncrypt)
                      ? "Qsm:pt-[calc(var(--graphic-byok-img-mobile-height)+var(--byok-padding-top)+var(--border))] pt-[calc(var(--graphic-byok-img-mobile-height)+var(--border)+var(--detail-page-status))] mx-auto max-w-max-content-width"
                      : "Qmd:pt-[calc(var(--graphic-byok-img-mobile-height)+var(--border))] pt-[var(--byok-confirm-padding)]"
                  }`}
                >
                  <BYOKconfirmation
                    byokProcessFailed={byokProcessFailed}
                    setCurrentDisplay={setCurrentDisplay}
                    byokProcessPending={byokProcessPending}
                    setCredsExists={setCredsExists}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ByokDashboard;
